import React from "react";

import { t, Trans } from "@lingui/macro";
import * as styles from "./PricePromo.module.scss";
import classNames from "classnames/bind";
import { LocationSpecificOffer } from "components/useLocationSpecificOffers/types";
import { OfferTokenValue, OfferTokenValues } from "types/api/exclusive_offers";
import { formattedText } from "components/OfferTokenizedContent/PlaceholderValueFormatter";
import { useLanguage } from "../../utils/localization/languages/I18nContext";
import { LanguageCode } from "../../utils/localization/languages/types";
import { i18n } from "@lingui/core";
import { Icon, IconName } from "@silversea-ssc/ui-components-react";

const cx = classNames.bind(styles);

export interface PriceHighlightsProps {
  offers: LocationSpecificOffer[];
  variant: "inline" | "list";
}

export interface PriceHighlight {
  order: number;
  label: string;
  icon: IconName;
}

type PriceHilightsIndex =
  | "ebb"
  | "freeeconomy"
  | "freebusiness"
  | "freeeconomynobc"
  | "nonuseaircredit";

type PriceHighlightLookup = Record<PriceHilightsIndex, PriceHighlight>;

// noinspection SpellCheckingInspection
const priceHighlights: PriceHighlightLookup = {
  ebb: {
    order: 1,
    label: t`10% Early booking bonus`,
    icon: "early-booking-bonus"
  },
  //This is all lowercase
  freeeconomy: {
    order: 2,
    label: t`free economy class air`,
    icon: "free-flight"
  },
  //This is all lowercase
  freebusiness: {
    order: 2,
    label: t`included business class air`,
    icon: "reduced-fares-in-business-class"
  },
  //This is all lowercase
  nonuseaircredit: {
    order: 2,
    label: t`special saving apply`,
    icon: "pack"
  },
  freeeconomynobc: {
    order: 2,
    label: t`free economy class air`,
    icon: "free-flight"
  }
};

const orderPriceHighlights = (
  a: PriceHighlightWithTokens & { priceHighlight: PriceHighlight },
  b: PriceHighlightWithTokens & { priceHighlight: PriceHighlight }
) => a.priceHighlight.order - b.priceHighlight.order;

interface PriceHighlightWithTokens {
  priceHighlight: PriceHighlight;
  label: string;
  tokenValues: OfferTokenValues;
}

function distinctHighlight(
  v: PriceHighlightWithTokens,
  i: number,
  a: PriceHighlightWithTokens[]
): boolean {
  return a.findIndex((x) => x.label === v.label) === i;
}

function toHighlightLabelWithToken(
  offer: LocationSpecificOffer & { highlightLabel: string }
): PriceHighlightWithTokens & { priceHighlight: PriceHighlight } {
  return {
    priceHighlight: priceHighlights[offer.highlightLabel],
    tokenValues: offer.tokenValues || {},
    label: offer.highlightLabel
  };
}

function availablePriceHighlight(
  offer: LocationSpecificOffer
): offer is LocationSpecificOffer & { highlightLabel: string } {
  return (
    !!offer.highlightLabel &&
    Object.keys(priceHighlights).includes(offer.highlightLabel)
  );
}

const PriceHighlights: React.FC<PriceHighlightsProps> = ({
  offers = [],
  variant
}) => {
  const htmlHighlights = (offers || [])
    .filter(availablePriceHighlight)
    .map(toHighlightLabelWithToken)
    .filter(distinctHighlight)
    .sort(orderPriceHighlights);

  const language = useLanguage();
  return (
    <div
      className={cx({
        inline: variant == "inline",
        list: variant == "list"
      })}
    >
      <React.Fragment>
        {htmlHighlights.map((highlight, i) => (
          <span className={styles.promo} key={highlight.priceHighlight.order}>
            {i == 0 && (
              <React.Fragment>
                <Trans id="with-promo">with</Trans>{" "}
              </React.Fragment>
            )}
            {htmlHighlights.length > 1 && i == htmlHighlights.length - 1 && (
              <React.Fragment> and </React.Fragment>
            )}
            {variant == "list" && (
              <Icon
                name={highlight.priceHighlight.icon}
                size="1.5x"
                padding="inline"
              />
            )}{" "}
            {replaceTokens(
              i18n._(highlight.priceHighlight.label),
              highlight.tokenValues,
              language.code
            )}
          </span>
        ))}
      </React.Fragment>
    </div>
  );
};

const replaceTokens = (
  label: string,
  tokenValues: OfferTokenValues,
  language: LanguageCode
): string => {
  return Object.keys(tokenValues).reduce(
    (s, tn) => replaceToken(s, tokenValues[tn], language),
    label
  );
};

const replaceToken = (
  label: string,
  tokenValue: OfferTokenValue,
  language: LanguageCode
): string => {
  return label.replace(
    `#${tokenValue.tokenName}#`,
    formattedText(tokenValue, language)
  );
};

export default React.memo(PriceHighlights);
